import type { AdminRecord } from '@/types/record'
import { isoDaysAgo, isoHoursAgo } from '@/utils/format'
import { intBetween, mulberry32 } from '@/utils/seed'
import { ADMIN_ROLES, ROLE_PERMISSIONS } from '@/data/roles'
import {
  buildAdmins,
  buildAlertRules,
  buildAlerts,
  buildAssets,
  buildAuditLogs,
  buildBenefits,
  buildCareTeam,
  buildDevices,
  buildDrugs,
  buildFaq,
  buildFamilies,
  buildHealthMetrics,
  buildFeedback,
  buildLinks,
  buildMembershipPlans,
  buildMessages,
  buildOrders,
  buildPlans,
  buildRecords,
  buildReports,
  buildTemplates,
  buildTickets,
  buildTips,
  buildUsers,
  HEALTH_TAGS,
  REGION_SEED,
  type AdminSeed,
  type FamilySeed,
  type RegionSeed,
  type UserSeed,
} from './base'

export * from './base'

export interface SeedDatabase {
  regions: AdminRecord[]
  users: AdminRecord[]
  families: AdminRecord[]
  care_team: AdminRecord[]
  drugs: AdminRecord[]
  medication_plans: AdminRecord[]
  medication_records: AdminRecord[]
  health_metrics: AdminRecord[]
  alert_rules: AdminRecord[]
  alerts: AdminRecord[]
  reports: AdminRecord[]
  devices: AdminRecord[]
  messages: AdminRecord[]
  templates: AdminRecord[]
  tips: AdminRecord[]
  faq: AdminRecord[]
  benefits: AdminRecord[]
  membership_plans: AdminRecord[]
  orders: AdminRecord[]
  links: AdminRecord[]
  assets: AdminRecord[]
  tickets: AdminRecord[]
  feedback: AdminRecord[]
  admins: AdminRecord[]
  roles: AdminRecord[]
  audit_logs: AdminRecord[]
  async_tasks: AdminRecord[]
  decoration_pages: AdminRecord[]
  decoration_configs: AdminRecord[]
  notification_deliveries: AdminRecord[]
  health_tags: AdminRecord[]
  drug_categories: AdminRecord[]
  device_models: AdminRecord[]
  faq_categories: AdminRecord[]
  link_categories: AdminRecord[]
  report_templates: AdminRecord[]
}

function uniqueText(values: unknown[]): string[] {
  return Array.from(new Set(values.map(value => String(value ?? '')).filter(Boolean)))
}

function buildRoles(admins: AdminSeed[]): AdminRecord[] {
  const builtin = ADMIN_ROLES.map((role, index) => ({
    id: `role-${role.code}`,
    code: role.code,
    name: role.name,
    description: role.description,
    data_scope: role.dataScope,
    data_scope_label: role.dataScopeLabel,
    permission_codes: ROLE_PERMISSIONS[role.code],
    permission_count: ROLE_PERMISSIONS[role.code].length,
    member_count: admins.filter(admin => admin.role_codes.includes(role.code)).length,
    is_builtin: true,
    status: 'active',
    created_at: isoDaysAgo(500 - index * 10, 9, 0),
    updated_at: isoDaysAgo(index + 2, 10, 0),
    row_version: 1,
  }))
  const custom: AdminRecord[] = [
    {
      id: 'role-region-operator',
      code: 'region_operator',
      name: '区域运营专员',
      description: '维护所辖区域用户、家庭与内容运营，不可查看审计与系统安全配置。',
      data_scope: 'region',
      data_scope_label: '所辖区域数据',
      permission_codes: ['dashboard.view', 'users.view', 'users.edit', 'families.view', 'families.edit', 'content.view', 'content.manage', 'messages.view', 'profile.view'],
      permission_count: 9,
      member_count: 2,
      is_builtin: false,
      status: 'active',
      created_at: isoDaysAgo(90, 10, 0),
      updated_at: isoDaysAgo(5, 11, 0),
      row_version: 1,
    },
    {
      id: 'role-content-reviewer',
      code: 'content_reviewer',
      name: '内容审核员',
      description: '审核健康贴士与帮助中心内容，可预览但不可发布会员方案。',
      data_scope: 'content',
      data_scope_label: '内容与素材',
      permission_codes: ['dashboard.view', 'content.view', 'content.manage', 'content.status', 'assets.view', 'links.view', 'profile.view'],
      permission_count: 7,
      member_count: 1,
      is_builtin: false,
      status: 'active',
      created_at: isoDaysAgo(60, 10, 0),
      updated_at: isoDaysAgo(3, 10, 30),
      row_version: 1,
    },
    {
      id: 'role-support-lead',
      code: 'support_lead',
      name: '客服主管',
      description: '管理客服队列、工单分配与升级，查看服务 SLA，不可修改业务主数据。',
      data_scope: 'support',
      data_scope_label: '客服队列与关联摘要',
      permission_codes: ['dashboard.view', 'users.view', 'families.view', 'messages.view', 'messages.send', 'membership.view', 'support.view', 'support.handle', 'support.assign', 'support.close', 'analytics.view', 'profile.view'],
      permission_count: 12,
      member_count: 1,
      is_builtin: false,
      status: 'disabled',
      created_at: isoDaysAgo(45, 14, 0),
      updated_at: isoDaysAgo(2, 9, 0),
      row_version: 1,
    },
  ]
  return [...builtin, ...custom]
}


function buildAsyncTasks(): AdminRecord[] {
  const random = mulberry32(7801)
  const types = ['导出用户', '导入药品', '生成健康报告', '发送消息', '设备固件下发', '导出审计日志']
  return Array.from({ length: 16 }, (_, index) => {
    const total = intBetween(random, 20, 480)
    const failed = index % 5 === 0 ? intBetween(random, 1, 6) : 0
    const status = index === 2 ? 'failed' : index === 4 ? 'running' : index === 6 ? 'queued' : failed ? 'partial' : 'success'
    return {
      id: `task-${index + 1}`,
      task_no: `RW2026${String(index + 1).padStart(4, '0')}`,
      task_type: types[index % types.length] as string,
      title: `${types[index % types.length]} · ${['今日筛选结果', '药品资料批次', '家庭健康月报', '秋季关怀消息', '血压计固件', '本月审计'][index % 6]}`,
      status,
      progress: status === 'success' || status === 'partial' || status === 'failed' ? 100 : status === 'running' ? intBetween(random, 20, 80) : 0,
      total,
      success: status === 'success' ? total : status === 'partial' ? total - failed : status === 'running' ? Math.floor(total * 0.6) : 0,
      failed,
      message: status === 'failed' ? '文件解析失败：第 18 行药品批准文号重复' : failed ? `${failed} 项未完成，可单独重试` : status === 'running' ? '正在处理，可离开页面稍后查看' : '任务已完成',
      created_by: ['林珊', '赵文博', '张明远', '周可欣'][index % 4] as string,
      created_at: isoHoursAgo(intBetween(random, 1, 48), intBetween(random, 0, 59)),
      finished_at: status === 'queued' || status === 'running' ? '' : isoHoursAgo(intBetween(random, 0, 2), 20),
      row_version: 1,
    }
  })
}

const DECORATION_PAGE_DEFS: Array<[string, string, string, string[]]> = [
  ['home', '首页', '/pages/home/index', ['健康概览', '快捷入口', '健康贴士', '家庭守护']],
  ['profile', '个人中心', '/pages/profile/index/index', ['用户信息', '会员卡片', '服务入口', '通知入口']],
  ['medication-plan', '用药计划', '/pages/medication/plan/index', ['今日用药', '用药时间轴', '提醒设置']],
  ['health-dashboard', '健康看板', '/pages/health/dashboard/index', ['指标概览', '趋势图表', '异常提示']],
  ['family', '家庭档案', '/pages/family/index/index', ['家庭概览', '成员列表', '守护设置']],
  ['help', '帮助中心', '/pages/profile/help/index', ['搜索框', '帮助分类', '热门问题']],
  ['report', '健康报告', '/pages/profile/report/index', ['报告列表', '报告详情', '导出入口']],
  ['notifications', '消息通知', '/pages/profile/notifications/index', ['消息分类', '消息列表']],
]

function buildDecoration(): { pages: AdminRecord[]; configs: AdminRecord[] } {
  const pages = DECORATION_PAGE_DEFS.map((item, index) => ({
    id: `dpage-${item[0]}`,
    page_key: item[0],
    title: item[1],
    full_path: item[2],
    edit_scope: item[3],
    sort: index + 1,
    status: 'active',
    created_at: isoDaysAgo(240 - index, 9, 0),
    updated_at: isoDaysAgo(index + 1, 10, 0),
    row_version: 1,
  }))
  const configs = DECORATION_PAGE_DEFS.map((item, index) => ({
    id: `dconfig-${item[0]}`,
    page_key: item[0],
    page_title: item[1],
    full_path: item[2],
    version: 3,
    status: 'published',
    saved_by: '林珊',
    saved_at: isoDaysAgo(index + 2, 15, 20),
    components: item[3].map((label, componentIndex) => ({
      id: `${item[0]}-${componentIndex + 1}`,
      type: ['banner', 'quick-entry', 'metric-card', 'tip-card', 'family-card', 'notice'][componentIndex % 6] as string,
      label,
      title: label,
      subtitle: componentIndex === 0 ? '把专业照护带到家人身边' : '按需配置展示内容',
      visible: true,
      sort: componentIndex + 1,
    })),
    created_at: isoDaysAgo(240 - index, 9, 0),
    updated_at: isoDaysAgo(index + 1, 10, 0),
    row_version: 3,
  }))
  return { pages, configs }
}

function buildDeliveries(messages: AdminRecord[], users: UserSeed[]): AdminRecord[] {
  const random = mulberry32(3202)
  const sent = messages.filter(message => message.status === 'sent')
  return sent.flatMap((message, messageIndex) =>
    Array.from({ length: 6 }, (_, index) => {
      const user = users[(messageIndex * 5 + index) % users.length] as UserSeed
      const failed = index === 4 && messageIndex % 3 === 0
      return {
        id: `delivery-${messageIndex}-${index}`,
        message_id: message.id,
        message_no: message.message_no,
        message_title: message.title,
        user_name: user.name,
        channel: (message.channels as string[])[index % (message.channels as string[]).length] as string,
        status: failed ? 'failed' : index < 3 ? 'read' : 'delivered',
        failure_reason: failed ? '运营商网关返回号码状态异常' : '',
        sent_at: isoHoursAgo(intBetween(random, 2, 60), intBetween(random, 0, 59)),
        read_at: index < 3 ? isoHoursAgo(intBetween(random, 1, 40), intBetween(random, 0, 59)) : '',
        created_at: isoHoursAgo(intBetween(random, 2, 60), 10),
        row_version: 1,
      }
    }),
  )
}

function applyRegionCounts(regions: RegionSeed[], users: UserSeed[], families: FamilySeed[]): void {
  for (const region of regions) {
    const userCount = users.filter(user => user.region_id === region.id).length
    const familyCount = families.filter(family => family.region_id === region.id).length
    region.resource_count = userCount + familyCount
    region.admin_scope_count = region.level === 'district' ? 1 : region.level === 'city' ? 2 : 3
  }
}

export function createSeedDatabase(): SeedDatabase {
  const users = buildUsers()
  const families = buildFamilies(users)
  const drugs = buildDrugs()
  const careTeam = buildCareTeam()
  const plans = buildPlans(users, drugs, careTeam)
  const records = buildRecords(plans)
  const rules = buildAlertRules()
  const alerts = buildAlerts(users, rules, careTeam)
  const reports = buildReports(users)
  const devices = buildDevices(users)
  const admins = buildAdmins()
  const roles = buildRoles(admins)
  const messages = buildMessages()
  const membershipPlans = buildMembershipPlans()
  const templates = buildTemplates()
  const tips = buildTips()
  const faq = buildFaq()
  const benefits = buildBenefits()
  const decoration = buildDecoration()
  const reportTemplates = uniqueText(reports.map(item => item.template_name))
  const faqCategories = uniqueText(faq.map(item => item.category_name))
  const drugCategories = uniqueText(drugs.map(item => item.category_name))
  const deviceModels = uniqueText(devices.map(item => item.model_name))
  const now = isoDaysAgo(0, 9, 0)
  applyRegionCounts(REGION_SEED, users, families)

  return {
    regions: REGION_SEED as unknown as AdminRecord[],
    users: users as unknown as AdminRecord[],
    families: families as unknown as AdminRecord[],
    care_team: careTeam as unknown as AdminRecord[],
    drugs: drugs as unknown as AdminRecord[],
    medication_plans: plans as unknown as AdminRecord[],
    medication_records: records as unknown as AdminRecord[],
    health_metrics: buildHealthMetrics(users),
    alert_rules: rules as unknown as AdminRecord[],
    alerts: alerts as unknown as AdminRecord[],
    reports: reports as unknown as AdminRecord[],
    devices: devices as unknown as AdminRecord[],
    messages,
    templates,
    tips,
    faq,
    benefits,
    membership_plans: membershipPlans,
    orders: buildOrders(users),
    links: buildLinks(),
    assets: buildAssets(),
    tickets: buildTickets(users),
    feedback: buildFeedback(users),
    admins: admins as unknown as AdminRecord[],
    roles,
    audit_logs: buildAuditLogs(admins),
    async_tasks: buildAsyncTasks(),
    decoration_pages: decoration.pages,
    decoration_configs: decoration.configs,
    notification_deliveries: buildDeliveries(messages, users),
    health_tags: HEALTH_TAGS.map((name, index) => ({
      id: `tag-${index + 1}`,
      name,
      description: `用于家庭档案、消息受众与随访筛选的${name}标签`,
      status: index === HEALTH_TAGS.length - 1 ? 'disabled' : 'active',
      sort: index + 1,
      created_at: now,
      updated_at: now,
      row_version: 1,
    })),
    drug_categories: drugCategories.map((name, index) => ({
      id: `drug-category-${index + 1}`,
      name,
      description: `${name}药品统一分类，供筛选、用药计划与药事审核复用`,
      status: 'active',
      sort: index + 1,
      created_at: now,
      updated_at: now,
      row_version: 1,
    })),
    device_models: deviceModels.map((name, index) => {
      const sample = devices.find(item => item.model_name === name)
      return {
        id: `device-model-${index + 1}`,
        name,
        model_code: `MD-${String(index + 1).padStart(3, '0')}`,
        device_type: sample?.device_type ?? '血压计',
        capabilities: sample?.device_type === '血压计' ? ['血压', '心率'] : sample?.device_type === '血糖仪' ? ['血糖'] : ['心率'],
        status: index === 6 ? 'disabled' : 'active',
        sort: index + 1,
        created_at: now,
        updated_at: now,
        row_version: 1,
      }
    }),
    faq_categories: faqCategories.map((name, index) => ({
      id: `faq-category-${index + 1}`,
      name,
      description: `${name}相关帮助内容`,
      sort: index + 1,
      status: 'active',
      created_at: now,
      updated_at: now,
      row_version: 1,
    })),
    link_categories: LINK_CATEGORIES.map((name, index) => ({
      id: `link-category-${index + 1}`,
      name,
      sort: index + 1,
      status: 'active',
      created_at: now,
      updated_at: now,
      row_version: 1,
    })),
    report_templates: reportTemplates.map((name, index) => ({
      id: `report-template-${index + 1}`,
      name,
      report_type: index % 3 === 0 ? '周报' : index % 3 === 1 ? '月报' : '季度报告',
      description: '统一指标口径与版式，发送后用户端可见',
      status: index === 3 ? 'disabled' : 'active',
      sort: index + 1,
      created_at: now,
      updated_at: now,
      row_version: 1,
    })),
  }
}

export const ASSET_FOLDERS = ['首页素材', '内容封面', '药品资料', '帮助中心']
export const LINK_CATEGORIES = ['首页导航', '个人中心', '运营位', '帮助中心']
export const METRIC_TYPES = ['血压', '血糖', '心率', '血氧', '体温', '体重']
export const SEVERITIES = ['低', '中', '高']
export const DISEASE_TAGS = ['高血压', '2型糖尿病', '冠心病', '骨质疏松', '慢阻肺', '高血脂', '房颤', '认知障碍']
export const USER_OPTION_LIMIT = 200
